import React from "react";

const _regex = /\${([^}]+)}/g;

export type ReactReturnValue = string | React.ReactElement;
export type ReactValue = string | number | React.ReactElement;
type LookUpFunction<R = string> = (id: string) => R;
type LookUpDict<R = string> = Record<string | number, R>;
export type FormatValues<R = string> = R | LookUpDict<R> | LookUpFunction<R> | R[];

export function formatJSX(text: string, v: FormatValues<string>, options?: { regex?: RegExp }): string;
export function formatJSX(text: string, v: FormatValues<ReactValue>, options?: { regex?: RegExp }): ReactReturnValue;
export function formatJSX(text: string, v: FormatValues<ReactValue>, options?: { regex?: RegExp }): ReactReturnValue {
  if (!text)
    return "";

  const regex = options?.regex || _regex;

  if (typeof v === "function")
    return formatByFunctionJSX(text, v, regex);
  else if (Array.isArray(v)) {
    return formatByArrayJSX(text, v, regex);
  } else if (typeof v === "string" || typeof v === "number" || React.isValidElement(v)) {
    return formatByArrayJSX(text, [v], regex);
  }

  return formatByDictJSX(text, v as LookUpDict<ReactValue>, regex);
}

function formatByFunctionJSX(text: string, fn: LookUpFunction<ReactValue>, regex: RegExp): ReactReturnValue {
  const splits = text.split(regex);
  const results: ReactReturnValue[] = [];
  let hasReact = false;
  for (let i = 0; i < splits.length; i++) {
    if (i % 2 === 1) {
      const id = splits[i];
      const v = fn(id);
      const isReact = React.isValidElement(v); // typeof v !== "string" && typeof v !== "number";
      if (isReact) {
        hasReact = true;
        results.push(React.cloneElement(v, { key: i }));
      } else {
        results.push(v.toString());
      }
    } else {
      results.push(splits[i]);
    }
  }

  if (hasReact)
    return <>{results}</>
  return results.join("");
}

function formatByArrayJSX(text: string, values: ReactValue[], regex: RegExp): ReactReturnValue {
  const splits = text.split(regex);
  const results: ReactReturnValue[] = [];
  let hasReact = false;
  for (let i = 0; i < splits.length; i++) {
    if (i % 2 === 1) {
      const id = splits[i];
      if (id) {
        const index = +id;
        if (index >= 0 && index < values.length) {
          const v = values[index];
          const isReact = React.isValidElement(v); // typeof v !== "string" && typeof v !== "number";
          if (isReact) {
            hasReact = true;
            results.push(React.cloneElement(v, { key: i }));
          } else if (typeof v === "number")
            results.push(v.toString());
          else
            results.push(v);
        }
      } else {
        results.push(splits[i]);
      }
    } else {
      results.push(splits[i]);
    }
  }

  if (hasReact)
    return <>{results}</>
  return results.join("");
}

function formatByDictJSX(text: string, dict: LookUpDict<ReactValue>, regex: RegExp): ReactReturnValue {
  const splits = text.split(regex);
  const results: ReactReturnValue[] = [];
  let hasReact = false;
  for (let i = 0; i < splits.length; i++) {
    if (i % 2 === 1) {
      const id = splits[i];
      if (id in dict) {
        const v = (dict as any)[id];
        const isReact = typeof v !== "string" && typeof v !== "number";
        if (isReact) {
          hasReact = true;
          results.push(React.cloneElement(v, { key: i }));
        } else {
          results.push(v.toString());
        }
      } else {
        results.push(id);
      }
    } else {
      results.push(splits[i]);
    }
  }

  if (hasReact)
    return <>{results}</>
  return results.join("");
}